import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from 'components/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

import { useDialog } from 'components/Dialog';
import { useErrorHandler } from 'lib';

import { AddCityDialog_region$key } from '__generated__/AddCityDialog_region.graphql';
import { AddCityDialogMutation } from '__generated__/AddCityDialogMutation.graphql';

type FormFields = {
  name: string;
};

type Props = {
  region: AddCityDialog_region$key;
  connectionId: string;
};

const initial = {
  name: '',
};

const AddCityDialog: FC<Props> = ({ connectionId, ...props }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const { id } = useFragment(
    graphql`
      fragment AddCityDialog_region on Region {
        id
      }
    `,
    props.region
  );

  const [add, adding] = useMutation<AddCityDialogMutation>(graphql`
    mutation AddCityDialogMutation(
      $input: CreateCityInput!
      $connections: [ID!]!
    ) {
      addCityToRegion(input: $input) {
        node @prependNode(connections: $connections, edgeTypeName: "CityEdge") {
          ...CityRow_city
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: {
          input: {
            regionId: id,
            ...input,
          },
          connections: [connectionId],
        },

        ...errorHandler({
          onCompleted: close,
        }),
      });
    },
    [add, close, connectionId, errorHandler, id]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas miestas</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują miestą į regioną
          </DialogContentText>

          <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default AddCityDialog;
